import React from 'react';
import './App.css';
import TopNav from './components/TopNav';
import CalculatorView from './components/CalculatorView';
import ResultsView from './components/ResultsView';
import SavedRecipesView from './components/SavedRecipesView';
import IngredientsView from './components/IngredientsView';

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      selectedList: [],
      currentWeight: '',
      unitWeight: '',
      currentItem: null,
      currentProductName: '',
      result: null, // { yeild, cost, costPerUnit, totalUnit, breakdown, productName, alreadySaved }
      savedRecipes: JSON.parse(localStorage.getItem('savedRecipes') || '[]'),
      view: 'calculator',
    };

    this.handleSearch = this.handleSearch.bind(this);
    this.addToBucket = this.addToBucket.bind(this);
    this.removeFromBucket = this.removeFromBucket.bind(this);
    this.getCosting = this.getCosting.bind(this);
    this.resetCalculator = this.resetCalculator.bind(this);
    this.handleSaveRecipe = this.handleSaveRecipe.bind(this);
    this.handleDeleteRecipe = this.handleDeleteRecipe.bind(this);
    this.handleViewSavedRecipe = this.handleViewSavedRecipe.bind(this);
    this.handleAddIngredient = this.handleAddIngredient.bind(this);
    this.handleUpdateIngredient = this.handleUpdateIngredient.bind(this);
    this.handleDeleteIngredient = this.handleDeleteIngredient.bind(this);
    this.handleResetIngredients = this.handleResetIngredients.bind(this);
  }

  componentDidMount() {
    const stored = localStorage.getItem('ingredients');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0 && !isNaN(parsed[0].name)) {
        // Names are numeric strings — corrupted, re-fetch from source
        this.fetchAndStoreIngredients();
      } else if (Array.isArray(parsed)) {
        this.setState({ items: parsed });
      } else {
        // Old object format — convert and re-save
        const items = this.parseDataJson(parsed);
        localStorage.setItem('ingredients', JSON.stringify(items));
        this.setState({ items });
      }
    } else {
      this.fetchAndStoreIngredients();
    }
  }

  // ── Helpers ─────────────────────────────────────

  fetchAndStoreIngredients() {
    fetch('./data.json')
      .then(res => res.json())
      .then(result => {
        const items = result.map((item, i) => ({
          id: i + 1,
          name: item.name,
          price: String(item.price),
          vol: String(item.vol),
          unit: item.unit || 'grams',
        }));
        localStorage.setItem('ingredients', JSON.stringify(items));
        this.setState({ items });
      });
  }

  saveItemsToStorage(items) {
    localStorage.setItem('ingredients', JSON.stringify(items));
    this.setState({ items });
  }

  // ── Calculator ───────────────────────────────────

  handleSearch(item) { this.setState({ currentItem: item }); }

  addToBucket() {
    const { currentItem, currentWeight, selectedList } = this.state;
    if (!currentItem || !currentWeight) return;
    this.setState({
      selectedList: [...selectedList, { ...currentItem, weight: currentWeight }],
      currentWeight: '',
    });
  }

  removeFromBucket(index) {
    this.setState({ selectedList: this.state.selectedList.filter((_, i) => i !== index) });
  }

  getCosting() {
    const { selectedList, unitWeight, currentProductName } = this.state;
    if (selectedList.length === 0) return;

    let yeild = 0, totalCost = 0;
    const breakdown = [];

    for (const item of selectedList) {
      const itemCost = (item.price / item.vol) * item.weight;
      yeild += parseInt(item.weight);
      totalCost += itemCost;
      breakdown.push({ name: item.name, weight: item.weight, cost: itemCost.toFixed(2) });
    }

    totalCost = parseFloat(totalCost.toFixed(2));
    const costPerUnit = (totalCost / (yeild / parseInt(unitWeight))).toFixed(2);
    const totalUnit = (yeild / parseInt(unitWeight)).toFixed(2);

    this.setState({
      result: { productName: currentProductName, yeild, cost: totalCost, costPerUnit, totalUnit, breakdown, alreadySaved: false },
      view: 'results',
    });
  }

  resetCalculator() {
    this.setState({
      selectedList: [], currentWeight: '', unitWeight: '',
      currentItem: null, currentProductName: '',
      view: 'calculator',
    });
  }

  // ── Saved recipes ────────────────────────────────

  handleSaveRecipe(recipe) {
    const updated = [recipe, ...this.state.savedRecipes];
    localStorage.setItem('savedRecipes', JSON.stringify(updated));
    this.setState({ savedRecipes: updated });
  }

  handleDeleteRecipe(id) {
    const updated = this.state.savedRecipes.filter(r => r.id !== id);
    localStorage.setItem('savedRecipes', JSON.stringify(updated));
    this.setState({ savedRecipes: updated });
  }

  handleViewSavedRecipe(recipe) {
    this.setState({
      result: {
        productName: recipe.name,
        yeild: recipe.yeild,
        cost: recipe.cost,
        totalUnit: recipe.totalUnit,
        costPerUnit: recipe.costPerUnit,
        breakdown: recipe.breakdown,
        alreadySaved: true,
      },
      view: 'results',
    });
  }

  // ── Ingredient management ────────────────────────

  handleAddIngredient(newItem) {
    const updated = [...this.state.items, newItem].sort((a, b) => a.name.localeCompare(b.name));
    this.saveItemsToStorage(updated);
  }

  handleUpdateIngredient(updatedItem) {
    const updated = this.state.items.map(i => i.id === updatedItem.id ? updatedItem : i);
    this.saveItemsToStorage(updated);
  }

  handleDeleteIngredient(id) {
    this.saveItemsToStorage(this.state.items.filter(i => i.id !== id));
  }

  handleResetIngredients() {
    localStorage.removeItem('ingredients');
    this.fetchAndStoreIngredients();
  }

  // ── Render ───────────────────────────────────────

  render() {
    const { items, selectedList, currentWeight, unitWeight, currentProductName, result, savedRecipes, view } = this.state;

    return (
      <div className="app">
        <TopNav
          view={view}
          savedCount={savedRecipes.length}
          onBrandClick={this.resetCalculator}
          onIngredientsClick={() => this.setState({ view: 'ingredients' })}
          onSavedClick={() => this.setState({ view: 'saved' })}
        />

        {view === 'calculator' && (
          <CalculatorView
            items={items}
            selectedList={selectedList}
            currentWeight={currentWeight}
            unitWeight={unitWeight}
            currentProductName={currentProductName}
            onSearch={this.handleSearch}
            onWeightChange={e => this.setState({ currentWeight: e.target.value })}
            onUnitChange={e => this.setState({ unitWeight: e.target.value })}
            onProductNameChange={e => this.setState({ currentProductName: e.target.value })}
            onAddToBucket={this.addToBucket}
            onRemoveFromBucket={this.removeFromBucket}
            onCalculate={this.getCosting}
          />
        )}

        {view === 'results' && result && (
          <ResultsView
            productName={result.productName}
            yeild={result.yeild}
            cost={result.cost}
            costPerUnit={result.costPerUnit}
            totalUnit={result.totalUnit}
            breakdown={result.breakdown}
            alreadySaved={result.alreadySaved}
            onSave={this.handleSaveRecipe}
            onNewRecipe={this.resetCalculator}
          />
        )}

        {view === 'saved' && (
          <SavedRecipesView
            savedRecipes={savedRecipes}
            onViewRecipe={this.handleViewSavedRecipe}
            onDeleteRecipe={this.handleDeleteRecipe}
            onNewRecipe={this.resetCalculator}
          />
        )}

        {view === 'ingredients' && (
          <IngredientsView
            items={items}
            onAdd={this.handleAddIngredient}
            onUpdate={this.handleUpdateIngredient}
            onDelete={this.handleDeleteIngredient}
            onReset={this.handleResetIngredients}
          />
        )}

        <footer className="app-footer">&copy; {new Date().getFullYear()} Ophelia. All rights reserved.</footer>
      </div>
    );
  }
}

export default App;
